import { Box, getVariant } from '@apia/theme';
import FiltersSetterController from './FiltersSettersController';
import { Select, Input, Form } from '@apia/validations';
import { observer } from 'mobx-react-lite';
import DateFilterSetter from './DateFilterSetter';
import { SimpleButton } from '../../../../common';
import { useAppDispatch } from '../../../../../hooks/hooks';
import { queryActions } from '../../../../../store/querySlice';
import useCurrentListId from '../../../common/lists/hooks/useCurrentListId';
import store from '../../../../../store';
import { calculateDate } from '../FiltersSetter';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useDebouncedCallback } from '@apia/util';
import SelectFilterSetter from './SelectFilterSetter';
import InputFilterSetter from './InputFilterSetter';
import { FieldLabel, RequiredMark } from '@apia/components';
import { getLabel } from '../../../../../utils/util';
import queriesQueue from '../../../../../store/querySlice/queueController';

const FiltersSetterForm = observer(
  ({
    controller,
    type,
  }: {
    controller: FiltersSetterController;
    type: string;
  }) => {
    const dispatch = useAppDispatch();
    const fieldId = useCurrentListId();

    const dateFormat = window.strDateFormat.toLocaleUpperCase();

    const generateFiltersToSave = useCallback(() => {
      const returnObj = controller.filters.map((f) => {
        if (f.type === 'date') {
          if (f.hideToFilter) {
            const dateFrom = calculateDate({
              amountOf: f.dateFrom.amountOf,
              chkFromStartOfValue: f.chkFromStartOf,
              fixedValue: f.dateFrom.fixedValue,
              fromStartOfValue: f.dateFrom.fromStartOfValue,
            });
            return {
              apiaFilter: {
                ...f.filter,
                currentValue: dateFrom,
                savedValue: JSON.stringify({
                  dateFrom: f.dateFrom,
                  chkFromStartOf: f.chkFromStartOf,
                }),
              },
            };
          }
          let dateFrom = '';
          if (f.chkFromStartOf) {
            dateFrom = calculateDate({
              amountOf: f.dateFrom.amountOf,
              chkFromStartOfValue: f.chkFromStartOf,
              fromStartOfValue: f.dateFrom.fromStartOfValue,
              fixedValue: f.dateFrom.fixedValue,
            });
          } else if (f.dateFrom.fixedValue) {
            dateFrom = f.dateFrom.fixedValue;
          }
          let dateTo = '';
          if (f.chkFromStartOf) {
            dateTo = dayjs().startOf('day').format(dateFormat);
          } else if (f.dateTo!.fixedValue) {
            dateTo = f.dateTo!.fixedValue;
          }
          return {
            apiaFilter: {
              ...f.filter,
              currentValue: dateFrom,
              filterToValue: dateTo,
              savedValue: JSON.stringify({
                dateFrom: f.dateFrom,
                dateTo: { fixedValue: f.dateTo?.fixedValue },
                chkFromStartOf: f.chkFromStartOf,
              }),
            },
          };
        } else if (f.type === 'select') {
          return {
            apiaFilter: {
              ...f.filter,
              currentValue: f.value,
              options: f.options,
            },
          };
        }
        return {
          apiaFilter: {
            ...f.filter,
            currentValue: f.value,
          },
        };
      });
      return returnObj;
    }, [controller.filters, dateFormat]);

    const setFilters = async () => {
      await dispatch(
        queryActions.setFilters({
          fieldId,
          onlyRedux: false,
          filters: generateFiltersToSave(),
          avoidNotify: true,
        }),
      );
    };
    const handleChangingFilter = useDebouncedCallback(
      async (value: string, i: number) => {
        queriesQueue.runAction('directSetFilters', setFilters);
        const currentFilter = controller.filters[i];
        const runAutomatically = currentFilter.filter.runAutomatically === true;
        if (currentFilter.detectOnChange || runAutomatically) {
          await queriesQueue.runAction(
            'directUpdateFilters',
            async () => {
              await dispatch(
                queryActions.updateFilters(
                  fieldId === 'userQuery'
                    ? {
                        fieldId,
                        filters: [
                          {
                            ...currentFilter.filter,
                            currentValue: value,
                          },
                        ],
                        forceSearch: runAutomatically,
                        fromFiltersSetters: true,
                      }
                    : {
                        fieldId,
                        filters: [
                          {
                            ...currentFilter.filter,
                            currentValue: value,
                          },
                        ],
                        forceSearch: runAutomatically,
                        fromFiltersSetters: true,
                        query: {
                          workingMode:
                            fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
                        },
                      },
                ),
              ).finally(() => {
                queriesQueue.runAction(
                  'directUpdateFilters',
                  async () => {
                    const tableFilters =
                      store.getState().querySlice.queries[fieldId].filters;
                    if (tableFilters) {
                      controller.updateFilters(
                        tableFilters.map((f) => {
                          return f.apiaFilter;
                        }),
                      );
                    }
                    setFilters();
                  },
                  ['fromUpdateFilters', 'fromSearchfromUpdateFilters'],
                );
              });
            },
            ['fromUpdateFilters', 'fromSearchfromUpdateFilters'],
          );
        }
      },
    );

    return (
      <Form className="form_container" name="FiltersSetter" unregisterOnUnmount>
        <Box {...window.getVariant('layout.query.filtersSetters')}>
          <Box className="savedFilters_container">
            <Box
              className="newFilterGroupName__container"
              sx={{
                display: type === 'create' ? 'block' : 'none',
              }}
            >
              <Input
                type="text"
                name="newFilterGroupName"
                className="newFilterGroupName"
                label={getLabel('lblNewFilterName').text}
                onChange={(ev) => {
                  controller.newFilterGroupName = ev.target.value;
                }}
              />
            </Box>
            <Box
              className="savedFiltersCombo__container"
              sx={{
                display: type === 'create' ? 'none' : 'flex',
              }}
            >
              <Select
                name="savedFilters"
                className="savedFiltersCombo"
                label={getLabel('lblLoadSavedFiltersValues').text}
                value={
                  controller.savedFilters.find((sf) => sf.selected)?.id ??
                  'empty'
                }
                onChange={async (ev) => {
                  if (ev.target.value === 'empty') {
                    controller.filters = controller.filters.map((fil) => {
                      if (fil.type === 'date') {
                        if (fil.hideToFilter) {
                          return {
                            ...fil,
                            chkFromStartOf: false,
                            dateFrom: {
                              fixedValue: '',
                              amountOf: 0,
                              fromStartOfValue: 'actualDay',
                            },
                          };
                        }
                        return {
                          ...fil,
                          chkFromStartOf: false,
                          dateFrom: {
                            fixedValue: '',
                            amountOf: 0,
                            fromStartOfValue: 'actualDay',
                          },
                          dateTo: {
                            fixedValue: '',
                          },
                        };
                      } else {
                        return { ...fil, value: '' };
                      }
                    });
                    controller.savedFilters = controller.savedFilters.map(
                      (saved) => {
                        return { ...saved, selected: false };
                      },
                    );

                    await dispatch(
                      queryActions.setFilters({
                        fieldId,
                        onlyRedux: false,
                        filters: generateFiltersToSave(),
                        avoidNotify: true,
                      }),
                    );
                  } else {
                    const selectedOption = controller.savedFilters.find(
                      (saved) => {
                        return saved.id === ev.target.value;
                      },
                    );
                    if (selectedOption) {
                      await controller.getSettedFilter(selectedOption.id);
                      controller.selectedFilterGroupName = selectedOption.name;
                      controller.savedFilters = controller.savedFilters.map(
                        (saved) => {
                          if (saved.id === selectedOption.id) {
                            return { ...saved, selected: true };
                          } else {
                            return { ...saved, selected: false };
                          }
                        },
                      );
                    }
                  }
                }}
                options={[
                  {
                    label: getLabel('lblSelectSavedFilter').text,
                    value: 'empty',
                  },
                  ...controller.savedFilters.map((saved) => {
                    return { value: saved.id, label: saved.name };
                  }),
                ]}
              ></Select>
              <SimpleButton
                disabled={
                  !controller.savedFilters.some((saved) => saved.selected)
                }
                onClick={async () => {
                  controller.deleteSavedFilter();
                }}
              >
                {getLabel('btnDel').text}
              </SimpleButton>
            </Box>
          </Box>
          {controller.filters.map((filter, i) => {
            return (
              <Box
                className={
                  filter.type === 'date'
                    ? 'dateFilter_container'
                    : 'filter_container'
                }
              >
                {filter.type === 'date' ? (
                  <Box
                    //as={'label'}
                    className={`fieldLabel`}
                    {...getVariant('layout.common.components.fieldLabel')}
                  >
                    <Box className="fieldLabel__label">
                      {(window.SHOW_REQUIRED_POSITION === '1'
                        ? 'before'
                        : 'after') === 'before' && (
                        <RequiredMark isRequired={filter.required} />
                      )}
                      {filter.title}
                      {(window.SHOW_REQUIRED_POSITION === '1'
                        ? 'before'
                        : 'after') === 'after' && (
                        <RequiredMark isRequired={filter.required} />
                      )}
                      :
                    </Box>
                    <Box className={'filterDateValue_container'}>
                      <DateFilterSetter
                        controller={controller}
                        filter={filter}
                        i={i}
                        setFilters={setFilters}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box className={'filterValue_container'}>
                    {filter.type === 'select' ? (
                      <FieldLabel
                        label={filter.title}
                        required={filter.required}
                        requiredMarkPosition={
                          window.SHOW_REQUIRED_POSITION === '1'
                            ? 'before'
                            : 'after'
                        }
                      >
                        <SelectFilterSetter
                          controller={controller}
                          filter={filter}
                          i={i}
                          handleChangingFilter={handleChangingFilter}
                        />
                      </FieldLabel>
                    ) : (
                      <FieldLabel
                        label={filter.title}
                        required={filter.required}
                        requiredMarkPosition={
                          window.SHOW_REQUIRED_POSITION === '1'
                            ? 'before'
                            : 'after'
                        }
                      >
                        <InputFilterSetter
                          controller={controller}
                          filter={filter}
                          i={i}
                          handleChangingFilter={handleChangingFilter}
                        />
                      </FieldLabel>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Form>
    );
  },
);

export default FiltersSetterForm;
